import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, FileUp, FileText, Check, History, X, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { FLAGS, fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty, openFile, RowSearch, useQueryFilter, matchesQuery, EmptyInline } from "@/pages/shared";
import GuestFormFields, { blankGuest as blank } from "@/components/GuestForm";
import OutstandingCard from "@/components/OutstandingCard";
import GuestInfoDialog from "@/components/GuestInfoDialog";

export default function Gasten() {
  const { t } = useLang();
  const [guests, setGuests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [docGuest, setDocGuest] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useQueryFilter();
  const [timeline, setTimeline] = useState(null);
  const [info, setInfo] = useState(null);

  const openTimeline = async (g) => {
    try { setTimeline((await api.get(`/guests/${g.id}/stays`)).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const [list, staying] = await Promise.all([api.get("/guests"), api.get("/reservations?filter=staying")]);
      const pitchMap = {};
      staying.data.forEach((r) => {
        if (r.guest_id) pitchMap[r.guest_id] = [...(pitchMap[r.guest_id] || []), r.pitch_label].filter(Boolean);
      });
      setGuests([...list.data]
        .map((g) => ({ ...g, current_pitches: pitchMap[g.id] || [] }))
        .sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const term = q.trim().toLowerCase();
  const visible = guests.filter((g) => !term || [g.first_name, g.last_name, g.email, g.phone,
    g.license_plate, g.company?.name, g.city, ...(g.current_pitches || [])]
    .some((v) => String(v || "").toLowerCase().includes(term)));

  const openNew = () => { setForm(blank); setEditId(null); setOpen(true); };
  const openEdit = (g) => { setForm({ ...blank, ...g }); setEditId(g.id); setOpen(true); };

  const save = async () => {
    try {
      if (editId) await api.put(`/guests/${editId}`, form);
      else await api.post("/guests", form);
      setOpen(false);
      await load();
      toast.success(t("saved"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/guests/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openDocs = async (g) => {
    setDocGuest(g);
    setDocs((await api.get(`/files?guest_id=${g.id}`)).data);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    const ALLOWED = ["pdf", "jpg", "jpeg", "png", "webp", "heic"];
    if (!ALLOWED.includes(ext)) { toast.error(t("upload_bad_type")); e.target.value = ""; return; }
    if (file.size > 15 * 1024 * 1024) { toast.error(t("upload_too_big")); e.target.value = ""; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "guest-doc");
    fd.append("guest_id", docGuest.id);
    try {
      await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((await api.get(`/files?guest_id=${docGuest.id}`)).data);
      toast.success(t("saved"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <Panel
      title={`${t("guests")} (${visible.length}${q ? `/${guests.length}` : ""})`}
      testid="guests-panel"
      action={
        <div className="flex items-center gap-2">
          <RowSearch value={q} onChange={setQ} placeholder={t("guests_search")} testid="guests-search" />
          <Button onClick={openNew} data-testid="add-guest-button"><Plus className="h-4 w-4 mr-1" />{t("new_guest")}</Button>
        </div>
      }
    >
      {visible.length === 0 ? <Empty /> : (
        <div className="max-h-[70vh] overflow-auto cb-scroll overscroll-contain" data-testid="guests-scroll">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card">
            <TableRow>
              <TableHead>{t("guest")}</TableHead>
              <TableHead>{t("phone")}</TableHead>
              <TableHead className="text-right">{t("stays")}</TableHead>
              <TableHead className="text-right">{t("documents")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((g) => (
              <TableRow key={g.id} data-testid={`guest-row-${g.id}`}>
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    {g.first_name} {g.last_name}
                    {g.rood_lintje && (
                      <Check className="h-4 w-4 shrink-0 text-rose-600" strokeWidth={3}
                        title={FLAGS.rood_lintje.nl} data-testid={`guest-ribbon-${g.id}`} />
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground">{g.phone}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openTimeline(g)} data-testid={`stays-${g.id}`}>
                    <History className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openDocs(g)} data-testid={`docs-${g.id}`}>
                    <FileText className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setInfo(g)} title={t("users_info")} data-testid={`info-guest-${g.id}`}><Info className="h-4 w-4 text-primary" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(g)} data-testid={`edit-guest-${g.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(g.id)} data-testid={`delete-guest-${g.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      )}

      {/* Guest form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="guest-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2">
            {editId ? t("edit") : t("new_guest")}
            {form.rood_lintje && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
                style={{ background: FLAGS.rood_lintje.color + "1f", color: FLAGS.rood_lintje.color }} data-testid="guest-dialog-ribbon">
                <Check className="h-3.5 w-3.5" strokeWidth={3} />{FLAGS.rood_lintje.nl}
              </span>
            )}
          </DialogTitle></DialogHeader>
          <GuestFormFields form={form} setForm={setForm} />
          {editId && <OutstandingCard guestId={editId} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="save-guest-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tijdlijn van de gast */}
      <Dialog open={!!timeline} onOpenChange={(o) => !o && setTimeline(null)}>
        <DialogContent data-testid="guest-timeline-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("stays")} — {timeline?.guest_name}</DialogTitle></DialogHeader>
          {timeline && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span><b className="text-foreground">{timeline.count}</b> {t("stays").toLowerCase()}</span>
                <span><b className="text-foreground">{timeline.nights_total}</b> {t("nights")}</span>
                {timeline.first && <span>{fmtDate(timeline.first)} → {fmtDate(timeline.last)}</span>}
              </div>
              {timeline.stays.length === 0 ? <Empty /> : (
                <div className="relative max-h-80 overflow-auto cb-scroll pl-4">
                  <span className="absolute left-1 top-1 bottom-1 w-px bg-slate-200" />
                  {timeline.stays.map((s) => (
                    <div key={s.id} className="relative py-2" data-testid={`stay-${s.id}`}>
                      <span className="absolute -left-3 top-3.5 h-2.5 w-2.5 rounded-full ring-2 ring-white"
                        style={{ background: s.when === "now" ? "#e11d48" : s.when === "future" ? "#f59e0b" : "#94a3b8" }} />
                      <p className="text-sm font-semibold text-foreground">
                        {s.pitch_label} · {fmtDate(s.arrival)} → {fmtDate(s.departure)}
                        <span className="ml-2 text-[11px] font-normal text-muted-foreground">{s.nights} {t("nights")} · {s.persons} {t("persons").toLowerCase()}</span>
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {s.when === "now" ? t("stay_now") : s.when === "future" ? t("stay_future") : t("stay_past")}
                        {s.moved_to_pitch ? ` · ${t("move")} → ${s.moved_to_pitch}` : ""}
                        {s.moved_from_pitch ? ` · ${s.moved_from_pitch} → ${s.pitch_label}` : ""}
                        {s.move_reason ? ` · ${s.move_reason}` : ""}
                        {s.notes ? ` · ${s.notes}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Documents */}
      <Dialog open={!!docGuest} onOpenChange={(o) => !o && setDocGuest(null)}>
        <DialogContent data-testid="guest-docs-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("documents")} — {docGuest?.first_name} {docGuest?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg py-6 cursor-pointer hover:border-primary transition-colors">
              <FileUp className="h-5 w-5 text-primary" />
              <span className="text-sm text-muted-foreground">{uploading ? "…" : t("upload")}</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,application/pdf,image/*" className="hidden" onChange={upload} data-testid="guest-doc-input" />
            </label>
            <p className="text-[11px] text-muted-foreground text-center" data-testid="guest-doc-hint">{t("upload_hint")}</p>
            <div className="space-y-2 max-h-64 overflow-auto cb-scroll">
              {docs.length === 0 ? <EmptyInline /> :
                docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
                    <button className="text-sm text-primary hover:underline truncate" onClick={() => openFile(api, d.id)}>{d.original_filename}</button>
                    <span className="text-[11px] text-muted-foreground">{Math.round((d.size || 0) / 1024)} KB</span>
                  </div>
                ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {info && <GuestInfoDialog guest={info} onClose={() => setInfo(null)} />}
    </Panel>
  );
}
